/**
 * The one breakpoint the app reasons about: 880 px, the width at which the
 * route table becomes per-leg cards and the map/form split becomes a toggle.
 * Kept in one place so the CSS media query and the JS never drift apart.
 *
 * Two flavours of "narrow", and the difference matters:
 *   · {@link useNarrow} asks the WINDOW — that is what decides whether the map
 *     replaces the form instead of sitting beside it, and which chrome the top
 *     bar shows.
 *   · {@link usePaneNarrow} asks the ELEMENT — the form pane is ~500 px wide in
 *     an iPad-landscape split view on a 1024 px window, and the 12-column route
 *     table is unusable long before `window.matchMedia` would admit it.
 */
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';

export const NARROW_PX = 880;
export const NARROW_QUERY = `(max-width: ${NARROW_PX}px)`;

export function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

/**
 * Measures the pane it is attached to. Returns a callback ref to put on the
 * container plus the answer; before the first measurement (and without a
 * ResizeObserver) it falls back to the window, so the value is never wrong-by-
 * default on a phone.
 */
export function useNarrowPane(): { ref: (el: HTMLElement | null) => void; narrow: boolean } {
  const windowNarrow = useNarrow();
  const [paneNarrow, setPaneNarrow] = useState<boolean | null>(null);
  const obs = useRef<ResizeObserver | null>(null);

  const ref = useCallback((el: HTMLElement | null) => {
    obs.current?.disconnect();
    obs.current = null;
    if (!el) {
      setPaneNarrow(null);
      return;
    }
    const read = () => {
      const w = el.getBoundingClientRect().width;
      // a pane that is momentarily 0 wide (mounting, display:none) says nothing
      if (w > 0) setPaneNarrow(w <= NARROW_PX);
    };
    read();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    obs.current = ro;
  }, []);

  useEffect(() => () => obs.current?.disconnect(), []);

  return { ref, narrow: paneNarrow ?? windowNarrow };
}

/**
 * The pane answer, handed down to whatever renders inside it. `null` means
 * "nobody measured a pane" — then {@link usePaneNarrow} asks the window.
 */
const PaneNarrowContext = createContext<boolean | null>(null);
export const PaneNarrowProvider = PaneNarrowContext.Provider;

export function usePaneNarrow(): boolean {
  const pane = useContext(PaneNarrowContext);
  const windowNarrow = useNarrow();
  return pane ?? windowNarrow;
}

/**
 * `--vvh`: the height the browser is really showing, in px.
 *
 * `100vh` is the layout viewport, which on a phone does not shrink when the
 * on-screen keyboard opens — a full-height map plus a bottom sheet with a text
 * field in it would put that field under the keyboard. `visualViewport.height`
 * is the number that does move, and it equals `innerHeight` everywhere else,
 * so wide screens are unaffected.
 */
export function useViewportHeightVar(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    const set = () => {
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty('--vvh', Math.round(h) + 'px');
    };
    set();
    window.addEventListener('resize', set);
    window.addEventListener('orientationchange', set);
    vv?.addEventListener('resize', set);
    return () => {
      window.removeEventListener('resize', set);
      window.removeEventListener('orientationchange', set);
      vv?.removeEventListener('resize', set);
    };
  }, []);
}
